/* eslint-disable no-plusplus */
import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';

/**
 * A template component that displays grouped content with styled section headers
 * and a grid layout for each group's items.
 *
 * **Data Structure:**
 * The `groupedData` prop expects an object where:
 * - Keys are group names (strings) that will be displayed as section headers
 * - Values are arrays of items to render within each group
 *
 * **Example Data Shape:**
 * ```js
 * const groupedData = {
 *   'Group Name 1': [
 *     { id: 1, name: 'Item A', ... },
 *     { id: 2, name: 'Item B', ... },
 *   ],
 *   'Group Name 2': [
 *     { id: 3, name: 'Item C', ... },
 *   ],
 * };
 * ```
 *
 * **Usage Example:**
 * ```jsx
 * <GroupedCardContent
 *   groupedData={groupedByLocation}
 *   getItemKey={item => item.id}
 *   renderCard={item => <CustomCard data={item} onClick={handleClick} />}
 *   gridSpacing={2}
 *   containerSx={{ p: 3 }}
 * />
 * ```
 *
 * @param {Object} groupedData - Object where keys are group names (strings) and values are arrays of items
 * @param {Function} renderCard - Function that takes an item and returns a React element to render
 * @param {Function} getItemKey - Function that takes an item and returns a unique key (string or number)
 * @param {Object} [containerSx] - Optional sx props for the outer container (default: { p: 2, pt: 3 })
 * @param {number} [gridSpacing] - Spacing between grid items in theme spacing units (default: 0)
 */
export default function GroupedCardContent({
  groupedData,
  getGroupLink,
  renderGroupHeaderExtra,
  renderCard,
  getItemKey,
  containerSx = { p: 2, pt: 3 },
  gridSpacing = 0,
}) {
  let globalItemIndex = 0;

  // Safety check for groupedData
  // if (!groupedData || typeof groupedData !== 'object') {
  //   console.warn('GroupedCardContent: groupedData is null or not an object', groupedData);
  //   return null;
  // }

  // console.log('GroupedCardContent received:', {
  //   entries: Object.entries(groupedData),
  //   groupedData,
  //   keys: Object.keys(groupedData),
  // });

  return (
    <Box sx={containerSx}>
      {Object.entries(groupedData).map(([locationNameLong, products]) => {
        // console.log(`Processing location: ${locationNameLong}`, {
        //   isArray: Array.isArray(products),
        //   products,
        // });

        // Skip if products array is empty or null
        if (!products || !Array.isArray(products) || products.length === 0) {
          // console.warn(`Skipping ${locationNameLong} - no valid products`, products);
          return null;
        }

        const locationDetailsPath = getGroupLink
          ? getGroupLink(products[0], locationNameLong)
          : null;
        const groupHeaderExtra = renderGroupHeaderExtra
          ? renderGroupHeaderExtra(products[0], locationNameLong)
          : null;

        return (
          <Box key={locationNameLong} sx={{ mb: 4 }}>
            <Box sx={{ alignItems: 'center', display: 'flex', mb: 2, ml: 2 }}>
              <Typography
                component="div"
                sx={theme => ({
                  '&::after': {
                    background: theme.palette._next.primary.dark,
                    borderRadius: '1.5px',
                    content: '""',
                    display: 'block',
                    height: '3px',
                    marginTop: '4px',
                  },
                  ':hover': {
                    backgroundColor: theme.palette.secondary.light,
                  },
                  color: 'text.primary',
                  display: 'inline-block',
                  fontSize: '18px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  maxWidth: '90%',
                  pb: 0,
                  textTransform: 'uppercase',
                })}
                variant="h6"
              >
                <Box
                  {...(locationDetailsPath
                    ? {
                        component: RouterLink,
                        to: locationDetailsPath,
                      }
                    : {
                        component: 'span',
                      })}
                  sx={{
                    alignItems: 'center',
                    color: 'inherit',
                    display: 'inline-flex',
                    textDecoration: 'none',
                    textOverflow: 'ellipsis',
                    verticalAlign: 'bottom',
                  }}
                >
                  {locationNameLong}
                </Box>
              </Typography>
              {groupHeaderExtra}
            </Box>
            <Grid container spacing={gridSpacing}>
              {products?.map(product => (
                <Grid key={getItemKey(product)}>{renderCard(product, globalItemIndex++)}</Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}

GroupedCardContent.propTypes = {
  containerSx: PropTypes.shape({}),
  getGroupLink: PropTypes.func,
  getItemKey: PropTypes.func.isRequired,
  gridSpacing: PropTypes.number,
  groupedData: PropTypes.shape({}).isRequired,
  renderCard: PropTypes.func.isRequired,
  renderGroupHeaderExtra: PropTypes.func,
};

GroupedCardContent.defaultProps = {
  containerSx: { p: 2, pt: 3 },
  getGroupLink: null,
  gridSpacing: 0,
  renderGroupHeaderExtra: null,
};
